import { useCallback } from 'react';
import { sanitizeNumber } from '../utils';
import { TAB_INDICES, OR_750_TO_FIN_RATIO } from '../constants';

/**
 * Hook pour gérer la logique de soumission des formulaires
 */
export const useFormSubmit = ({
  tab,
  fetchMouvementsOr,
  fetchMouvementsOrClient,
  createMouvementOr,
  updateMouvementOr,
  createMouvementOrClient,
  updateMouvementOrClient,
  setEditRow,
  setLastEditedRowId,
  returnAfterEdit,
  setReturnAfterEdit,
  setTab
}) => {
  const handleSubmit = useCallback(async (values, editRow) => {
    if (tab === TAB_INDICES.CLAL) return; // CLAL utilise ClalForm.onAdd

    const entity = values.fournisseur || null;
    const fournisseurId = entity ? (entity.fournisseur_id ?? entity.four_id ?? entity.id) : null;
    const clientId = entity ? (entity.client_id ?? entity.cli_id ?? entity.id) : null;
    const entityId = (tab === TAB_INDICES.FOURNISSEURS) ? fournisseurId : clientId;
    
    const entreeSan = sanitizeNumber(values.entreeOrFin);
    const sortie750San = sanitizeNumber(values.sortieOr750);
    const sortieFinSan = sanitizeNumber(values.sortieOrFin);

    let entreeFinal = entreeSan;
    let sortie750Final = sortie750San;
    let sortieFinFinal = sortieFinSan;
    let perteFinal = sanitizeNumber(values.perte);

    // Cas fournisseur - garder la perte fournie par le formulaire
    if (tab === TAB_INDICES.FOURNISSEURS) {
      let sortieFinTh = sortie750Final !== null ? sortie750Final * OR_750_TO_FIN_RATIO : null;
      
      // Si sortieOrFin n'est pas fourni, utiliser la valeur théorique
      if (sortieFinFinal === null && sortieFinTh !== null) {
        sortieFinFinal = sortieFinTh;
      }
    }

    const base = {
      date: values.date,
      perte: perteFinal,
      designation: values.designation,
      entree_or_fin: entreeFinal,
      sortie_or_750: sortie750Final,
      sortie_or_fin: sortieFinFinal,
      metal_id: values.metal || 1
    };

    let payload = {};
    if (tab === TAB_INDICES.FOURNISSEURS) {
      payload = { ...base, fournisseur_id: entityId };
    } else if (tab === TAB_INDICES.CLIENTS) {
      payload = { ...base, client_id: entityId };
      if (sortie750Final > 0) {
        payload.sortie_or_fin = sortieFinFinal;
      }
    }

    try {
      if (tab === TAB_INDICES.FOURNISSEURS) {
        if (editRow) {
          await updateMouvementOr(editRow.id, payload);
        } else {
          await createMouvementOr(payload);
        }
        await fetchMouvementsOr();
      } else if (tab === TAB_INDICES.CLIENTS) {
        if (editRow) {
          await updateMouvementOrClient(editRow.id, payload);
        } else {
          await createMouvementOrClient(payload);
        }
        await fetchMouvementsOrClient();
      }

      if (editRow) {
        setLastEditedRowId(editRow.id);
        setTimeout(() => setLastEditedRowId(null), 2500);
      }
      
      setEditRow(null);
      
      if (returnAfterEdit !== null) {
        const dest = returnAfterEdit;
        setReturnAfterEdit(null);
        setTimeout(() => { 
          setTab(dest); 
          window.scrollTo({ top: 0, behavior: 'smooth' }); 
        }, 60);
      }
      
      setTimeout(() => { 
        const el = document.querySelector('input[name="date"]'); 
        if (el) el.focus(); 
      }, 60);
    } catch (e) {
      console.error(e);
      throw e;
    }
  }, [
    tab, 
    fetchMouvementsOr, 
    fetchMouvementsOrClient,
    createMouvementOr,
    updateMouvementOr,
    createMouvementOrClient,
    updateMouvementOrClient,
    setEditRow,
    setLastEditedRowId,
    returnAfterEdit,
    setReturnAfterEdit,
    setTab
  ]);

  return { handleSubmit };
};
